import { showNotification } from '@mantine/notifications';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import type { Automation } from '../../api';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import { Box, createStyles, Group, rem, useMantineTheme } from '@mantine/core';
import IconEmojiSelector from '@repo/common/components/IconEmojiSelector';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import { UserRole } from '@repo/common/enums/UserRole';
import { EntityType } from '@repo/common/enums/entityType';
import type { ColorNames } from '@repo/theme/utils';
import {
	useAutomationRun,
	useDeleteAutomation,
	useUpdateAutomation,
} from '../../api/hooks/automation';
import { automationQueryKeyFactory } from '../../api/hooks/automation/constants';
import { AutomationV2StatusColumn } from '../../components/AutomationV2/AutomationV2StatusColumn';
import { getCadenceDisplayLabel } from '../../components/ScheduleSelector/constants';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { useDeleteManyAction } from '../../components/TableV2/BaseModel.hooks';
import { ROW_HEIGHT } from '../../components/TableV2/TableV2.styles';
import { DateTimeRender } from '../../components/TableV2/render';
import { getEntityDisplayTitle } from '../../lib/utils/entity';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { AutomationToggle } from '../AutomationPage/AutomationToggle';

const useStyles = createStyles((theme) => {
	const hoverFillColor = 'fill/transparent/hover';
	const activeFillColor = 'fill/transparent/active';
	const hoverIconColor: ColorNames = 'icon/primary/hover';
	const paddingY: number = theme.other.space[1.5];
	const activeIconColor: ColorNames = 'icon/primary/active';
	const boxShadow = '';
	const boxShadowOnActive = '';

	return {
		container: {
			height: theme.other.space[7],
			width: theme.other.space[7],
			flexShrink: 0,
			padding: `${paddingY} 0`,
			position: 'relative',

			borderRadius: theme.other.space[2],

			boxShadow: boxShadow,
			transition: 'box-shadow 0.075s ease-in-out 0s',

			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',

			'&:hover': {
				color: theme.other.getColor(hoverIconColor),
				backgroundColor: theme.other.getColor(hoverFillColor),
			},
			'&:active': {
				color: theme.other.getColor(activeIconColor),
				backgroundColor: theme.other.getColor(activeFillColor),
				boxShadow: boxShadowOnActive,
				paddingTop: undefined,
				paddingBottom: undefined,
			},
			'&:focus': {
				outline: `solid ${theme.other.getColor('border/emphasis/default')} 2px`,
				outlineOffset: rem(theme.other.space[0.25]),
			},
		},
	};
});

export function useColumns(): ExtendedDataTableColumn<Automation>[] {
	const theme = useMantineTheme();
	const { classes } = useStyles();

	const { mutateAsync: updateAutomation } = useUpdateAutomation({});

	const handleIconChange = useCallback(
		(id: string) => async (value: string) => {
			await updateAutomation({
				data: {
					id: id,
					['icon']: value,
				},
			});
		},
		[updateAutomation]
	);

	const toggleCallBack = useCallback(
		async (automation: Automation, enabled: boolean) => {
			await updateAutomation({
				data: {
					id: automation.id,
					is_enabled: enabled,
				},
			});
		},
		[updateAutomation]
	);

	const columns = useMemo(
		() => [
			{
				accessor: 'title',
				titleLabel: 'Name',
				render: (record: Automation) => (
					<Group noWrap spacing={theme.spacing['xs']}>
						<Box
							className={classes.container}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
							}}
						>
							<IconEmojiSelector
								value={
									record.icon ??
									iconEmojiSelectorValueToString({
										iconName: 'bolt',
										iconColor: '#4a4a4a',
									})
								}
								onChange={handleIconChange(record.id)}
								iconSize={15}
								emojiSize={15}
								entityType={'automation'}
							/>
						</Box>
						<Text
							size="sm"
							sx={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								lineHeight: `${ROW_HEIGHT}px`,
							}}
							color="text/primary/default"
							fw={600}
						>
							{getEntityDisplayTitle(record)}
						</Text>
					</Group>
				),
			},

			{
				accessor: 'schedule',
				titleLabel: 'Schedule',
				render: (record: Automation) => {
					if (!record.schedule) {
						return '';
					}
					return getCadenceDisplayLabel(record.schedule);
				},
			},
			{
				accessor: 'last_run_at',
				titleLabel: 'Last run',
				render: (record: Automation) => (
					<DateTimeRender record={record} field="last_run_at" />
				),
			},
			{
				accessor: 'last_run_status',
				titleLabel: 'Last run status',
				render: (record: Automation) => (
					<AutomationV2StatusColumn automation={record} />
				),
			},
			{
				accessor: 'next_run_at',
				titleLabel: 'Next run',
				render: (record: Automation) => (
					<DateTimeRender record={record} field="next_run_at" />
				),
			},
			{
				accessor: 'is_enabled',
				titleLabel: 'State',
				navigate: false,
				render: (record: Automation) => (
					<AutomationToggle
						enabled={record.is_enabled ?? false}
						onChange={(enabled) => toggleCallBack(record, enabled)}
					/>
				),
				width: 70,
			},
		],
		[classes.container, handleIconChange, theme.spacing, toggleCallBack]
	);

	return columns;
}

export const useActions = (): ICommandListItem<Automation>[] => {
	const { hasPermission: hasDeletePermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Automations.Delete',
	});
	const { mutateAsync: runAutomation } = useAutomationRun();
	const { mutateAsync: deleteAutomation } = useDeleteAutomation({});
	const onDelete = useCallback(
		(id: string) => deleteAutomation({ id }),
		[deleteAutomation]
	);
	const { action: deleteManyAction } = useDeleteManyAction<Automation>({
		name: 'automation',
		queryKeys: automationQueryKeyFactory.allLists(),
		onDelete,
	});

	const handleRunAutomations = useCallback(
		async (entities: Automation[]) => {
			let automationIds =
				entities
					?.filter(({ is_enabled: isEnabled }) => isEnabled)
					.map(({ id }) => id) ?? [];

			showNotification({
				title: 'Automations running',
				message: 'All the selected automations have been triggered',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});

			// Create a promise array of all the automations that are being run.
			await Promise.all(
				automationIds.map((automationId) =>
					runAutomation({
						data: {
							automationId,
						},
					})
				)
			);
		},
		[runAutomation]
	);

	const actions = useMemo(() => {
		const baseActions = [
			{
				id: 'actions::run',
				title: 'Run automation',
				name: 'Run',
				iconName: 'refresh' as IconNames,
				hotkey: '/mp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleRunAutomations,
			},
		];

		// Only admins can delete automations
		if (hasDeletePermission) {
			// @ts-expect-error - deleteManyAction iconName can be null but baseActions expects IconNames
			baseActions.push(deleteManyAction);
		}

		return baseActions;
	}, [deleteManyAction, handleRunAutomations, hasDeletePermission]);

	return actions;
};
